import { createSelector } from '@reduxjs/toolkit';
import { FEATURE_TYPE, FEATURE_VALUE, selectAccountId, selectApp, SELECTOR, setSelector } from '@socialive/common';
import { selectTreatmentWithConfig } from '@splitsoftware/splitio-redux';
import selectSplitioRaw from './selectSplitioRaw';

export const parseFeature = ({ treatment, config: rawConfig }, defaultConfig) => {
  try {
    const json = JSON.parse(rawConfig) ?? defaultConfig;
    const config = typeof json === 'object' && !Array.isArray(json) ? { ...defaultConfig, ...json } : json;
    return [treatment, config];
  } catch (e) {
    return [treatment, defaultConfig ?? rawConfig];
  }
};

/**
 * @typedef {object} Feature
 * @property {FEATURE_TYPE} type
 * @property {string} value
 */
/**
 * A higher-order selector for retrieving the specified feature from the store.
 * @param {string|Feature} featureLike
 * @returns {Selector}
 */
const selectFeature = (featureLike) => {
  const feature = typeof featureLike === 'string' ? FEATURE_VALUE[featureLike] : featureLike;
  switch (feature?.type) {
    case FEATURE_TYPE.ACCOUNT:
      return createSelector(selectSplitioRaw, selectAccountId, (splitio, accountId) => {
        const raw = selectTreatmentWithConfig(splitio, feature.value, accountId);
        return parseFeature(raw, feature?.defaultConfig);
      });
    /** @deprecated All new features should be accountFeatures */
    case FEATURE_TYPE.APP:
      return createSelector(selectSplitioRaw, selectApp, (splitio, app) => {
        const raw = selectTreatmentWithConfig(splitio, feature.value, app);
        return parseFeature(raw, feature?.defaultConfig);
      });
    default:
      throw new Error(`Invalid FEATURE_TYPE ${feature?.type ?? JSON.stringify(featureLike)}`);
  }
};

setSelector(SELECTOR.FEATURE, selectFeature);

export default selectFeature;
